import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { ImagePlus, Repeat, X } from "lucide-react";
import { V } from "@/lib/vocab";

export const inputCls = "w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";
export const to24h = (s) => {
  if (!s) return "";
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  const m = s.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)?$/i);
  if (!m) return "";
  let h = parseInt(m[1], 10);
  const ap = (m[3] || "").toLowerCase();
  if (ap.startsWith("p") && h < 12) h += 12;
  if (ap.startsWith("a") && h === 12) h = 0;
  return h > 23 ? "" : `${String(h).padStart(2, "0")}:${m[2] || "00"}`;
};
export const to12h = (s) => {
  const m = (s || "").match(/^(\d{2}):(\d{2})$/);
  if (!m) return s || "";
  const h = parseInt(m[1], 10);
  return `${h % 12 || 12}:${m[2]} ${h >= 12 ? "PM" : "AM"}`;
};
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const NTH = [[1, "first"], [2, "second"], [3, "third"], [4, "fourth"], [-1, "last"]];

export const emptyForm = (settings) => ({
  id: null, title: "", description: "", date: "", time: "", end_time: "", category: "social", location: "",
  host_name: "", host_contact: "", capacity: "", rsvp_enabled: settings?.rsvp_default !== false, flyer_path: "",
  recurring: false, recurrence: { freq: "weekly", weekdays: [], interval: 1, nth: 1, weekday: 0, until: "" },
  series_id: null, apply_to: "this", announce: false,
});
export const formFrom = (e) => ({
  ...emptyForm(), id: e.id, title: e.title || "", description: e.description || "", date: e.date || "", time: to24h(e.time || ""),
  end_time: to24h(e.end_time || ""), category: e.category || "other", location: e.location || "", host_name: e.host_name || "",
  host_contact: e.host_contact || "", capacity: e.capacity ?? "", rsvp_enabled: !!e.rsvp_enabled, flyer_path: e.flyer_path || "",
  series_id: e.series_id || null, recurring: false, apply_to: e.series_id ? "this" : "this",
});

const Field = ({ label, hint, children, className = "" }) => (
  <div className={className}>
    <label className="text-sm font-semibold">{label}{hint && <span className="text-muted-foreground font-normal"> — {hint}</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);

// Create / edit one event or a whole series. `series` (recurrence text) is only for the banner.
export function EventEditor({ open, form, setForm, categories, settings, smsReady, saving, onSave, onClose, seriesText, tenantId }) {
  const f = form;
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const updRec = (k, v) => setForm((s) => ({ ...s, recurrence: { ...s.recurrence, [k]: v } }));
  const [uploading, setUploading] = useState(false);
  useEffect(() => { if (open && f && !f.id && f.recurring && f.date && !f.recurrence.weekdays.length) { const wd = (new Date(f.date + "T00:00:00").getDay() + 6) % 7; updRec("weekdays", [wd]); updRec("weekday", wd); } }, [f?.date, f?.recurring]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const uploadFlyer = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) { toast.error("Keep the flyer under 6 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try { const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); upd("flyer_path", r.data.path); toast.success("Flyer attached"); }
    catch (err) { toast.error(errMsg(err, "Upload failed")); }
    finally { setUploading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="event-editor">
        <DialogHeader>
          <DialogTitle className="font-heading">{f.id ? "Edit event" : "New event"}</DialogTitle>
          <DialogDescription>Your concierge learns this instantly — {V.lcs("person")} can ask about it by text or phone{settings?.rsvp_default !== false ? " and RSVP" : ""}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {f.id && f.series_id && (
            <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm" data-testid="event-series-scope">
              <div className="flex items-center gap-2 font-semibold text-sky-900"><Repeat className="h-4 w-4" /> Part of a series{seriesText ? ` · ${seriesText}` : ""}</div>
              <div className="mt-2 flex gap-2 text-xs font-semibold">
                <button type="button" data-testid="event-scope-this" onClick={() => upd("apply_to", "this")} className={`rounded-full px-3 py-1 border ${f.apply_to === "this" ? "bg-ink text-white border-ink" : "bg-white border-sand-300 text-ink/70"}`}>Just this date</button>
                <button type="button" data-testid="event-scope-series" onClick={() => upd("apply_to", "series")} className={`rounded-full px-3 py-1 border ${f.apply_to === "series" ? "bg-ink text-white border-ink" : "bg-white border-sand-300 text-ink/70"}`}>This and all future dates</button>
              </div>
            </div>
          )}
          <Field label="Title"><input data-testid="event-title" value={f.title} onChange={(e) => upd("title", e.target.value)} placeholder="Pool party" className={inputCls} /></Field>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field label="Date" className="col-span-2 md:col-span-1"><input data-testid="event-date" type="date" value={f.date} onChange={(e) => upd("date", e.target.value)} className={inputCls} /></Field>
            <Field label="Start"><input data-testid="event-time" type="time" value={f.time} onChange={(e) => upd("time", e.target.value)} className={inputCls} /></Field>
            <Field label="End" hint="optional"><input data-testid="event-end-time" type="time" value={f.end_time} onChange={(e) => upd("end_time", e.target.value)} className={inputCls} /></Field>
            <Field label="Theme">
              <select data-testid="event-category" value={f.category} onChange={(e) => upd("category", e.target.value)} className={inputCls}>
                {(categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            <Field label="Where"><input data-testid="event-location" value={f.location} onChange={(e) => upd("location", e.target.value)} placeholder="Clubhouse" className={inputCls} /></Field>
            <Field label="Host"><input data-testid="event-host" value={f.host_name} onChange={(e) => upd("host_name", e.target.value)} placeholder="Social committee" className={inputCls} /></Field>
            <Field label="Host contact" hint="phone or email"><input data-testid="event-host-contact" value={f.host_contact} onChange={(e) => upd("host_contact", e.target.value)} placeholder="(512) 555-0100" className={inputCls} /></Field>
          </div>
          <Field label="Details" hint={`what ${V.lcs("person")} hear when they ask`}>
            <textarea data-testid="event-description" rows={3} value={f.description} onChange={(e) => upd("description", e.target.value)} className={inputCls} placeholder="Bring a dish to share; the clubhouse opens at 5:30." />
          </Field>

          {/* RSVPs + capacity */}
          <div className="rounded-xl border border-sand-200 bg-sand-50/60 p-3 grid sm:grid-cols-2 gap-3 items-start">
            <label className="flex items-center justify-between gap-3 text-sm">
              <span><span className="font-semibold">Take RSVPs</span><span className="block text-xs text-muted-foreground">By text, phone or the portal.</span></span>
              <Switch data-testid="event-rsvp-enabled" checked={!!f.rsvp_enabled} onCheckedChange={(v) => upd("rsvp_enabled", v)} />
            </label>
            <Field label="Capacity" hint="blank = unlimited; extra parties join a waitlist">
              <input data-testid="event-capacity" type="number" min="1" max="5000" disabled={!f.rsvp_enabled} value={f.capacity} onChange={(e) => upd("capacity", e.target.value)} className={inputCls} placeholder="e.g. 40" />
            </Field>
          </div>

          {/* Flyer */}
          <div className="flex flex-wrap items-center gap-3" data-testid="event-flyer-block">
            {f.flyer_path && f.id && (
              <img alt="" src={`${process.env.REACT_APP_BACKEND_URL}/api/public/tenant/${tenantId}/events/${f.id}/flyer`} onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="h-14 w-20 object-cover rounded-lg border border-sand-200" />
            )}
            {f.flyer_path ? <span className="text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1" data-testid="event-flyer-set">Flyer attached ✓</span>
              : <span className="text-xs text-muted-foreground">No flyer — optional, shows in the portal and announcement email.</span>}
            <label className="rounded-full border border-sand-200 bg-white px-3.5 py-1.5 text-xs font-bold text-ink hover:border-ink cursor-pointer inline-flex items-center gap-1.5">
              <ImagePlus className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : f.flyer_path ? "Replace flyer" : "Upload flyer"}
              <input data-testid="event-flyer-input" type="file" accept="image/*" className="hidden" onChange={uploadFlyer} />
            </label>
            {f.flyer_path && <button type="button" data-testid="event-flyer-remove" onClick={() => upd("flyer_path", "")} className="text-xs font-bold text-muted-foreground hover:text-danger-600 inline-flex items-center gap-1"><X className="h-3 w-3" /> Remove</button>}
          </div>

          {/* Recurrence (create only) */}
          {!f.id && (
            <div className="rounded-xl border border-sand-200 bg-sand-50/60 p-3 space-y-2" data-testid="event-recurrence-block">
              <label className="flex items-center justify-between gap-3 text-sm">
                <span className="flex items-center gap-2 font-semibold"><Repeat className="h-4 w-4 text-ink/50" /> Repeats</span>
                <Switch data-testid="event-recurring" checked={!!f.recurring} onCheckedChange={(v) => upd("recurring", v)} />
              </label>
              {f.recurring && (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <select data-testid="event-rec-freq" value={f.recurrence.freq} onChange={(e) => updRec("freq", e.target.value)} className="rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white">
                      <option value="weekly">Every week</option><option value="biweekly">Every other week</option>
                      <option value="monthly">Monthly on this date</option><option value="monthly_nth">Monthly on the…</option><option value="daily">Every day</option>
                    </select>
                    {f.recurrence.freq === "monthly_nth" && (
                      <>
                        <select data-testid="event-rec-nth" value={f.recurrence.nth} onChange={(e) => updRec("nth", Number(e.target.value))} className="rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white">
                          {NTH.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <select data-testid="event-rec-weekday" value={f.recurrence.weekday} onChange={(e) => updRec("weekday", Number(e.target.value))} className="rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white">
                          {DOW.map((d, i) => <option key={d} value={i}>{d}day</option>)}
                        </select>
                      </>
                    )}
                    <span className="text-xs text-muted-foreground">until</span>
                    <input data-testid="event-rec-until" type="date" value={f.recurrence.until} onChange={(e) => updRec("until", e.target.value)} className="rounded-xl border border-sand-200 px-3 py-2 text-sm bg-white" />
                    <span className="text-xs text-muted-foreground">(blank = keeps going, ~8 months ahead at a time)</span>
                  </div>
                  {(f.recurrence.freq === "weekly" || f.recurrence.freq === "biweekly") && (
                    <div className="flex flex-wrap gap-1.5" data-testid="event-rec-weekdays">
                      {DOW.map((d, i) => {
                        const on = f.recurrence.weekdays.includes(i);
                        return <button key={d} type="button" data-testid={`event-rec-day-${i}`} aria-pressed={on}
                          onClick={() => updRec("weekdays", on ? f.recurrence.weekdays.filter((x) => x !== i) : [...f.recurrence.weekdays, i].sort())}
                          className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${on ? "bg-ink text-white border-ink" : "bg-white border-sand-300 text-ink/60"}`}>{d}</button>;
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {!f.id && (
            <label className="flex items-center justify-between gap-3 text-sm rounded-xl border border-sand-200 p-3">
              <span className="min-w-0"><span className="font-semibold">Announce it right away</span><span className="block text-xs text-muted-foreground">Text{smsReady ? "" : ` (no ${V.lc("org")} number yet)`} and/or email everyone, per the Events module defaults. You can always announce later.</span></span>
              <Switch data-testid="event-announce-now" checked={!!f.announce} onCheckedChange={(v) => upd("announce", v)} />
            </label>
          )}
        </div>
        <DialogFooter>
          <button data-testid="event-cancel" onClick={onClose} className="rounded-full font-semibold px-5 py-2.5 border border-sand-200 hover:bg-sand-50 transition-colors">Cancel</button>
          <button data-testid="event-save" onClick={onSave} disabled={saving || !f.title.trim() || (f.recurring && !f.date)}
            className="rounded-full font-semibold px-6 py-2.5 text-white bg-redline-500 hover:bg-redline-600 disabled:opacity-60 transition-colors">
            {saving ? "Saving…" : f.id ? "Save changes" : f.recurring ? "Create series" : "Create event"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
